import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Container, Box, Typography, TextField, InputAdornment, IconButton, Button,
  Paper, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TableSortLabel, Modal, Snackbar, Alert, Stack, FormControl, InputLabel,
  Select, MenuItem, Switch, Tooltip, useTheme, CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlaylistAdd as BulkAddIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
  InfoOutlined as InfoOutlineIcon,
} from '@mui/icons-material';
import { useBranch } from './../../layouts/BranchContext'; 
import { styled } from '@mui/system';

// Keep your existing imports
// import CustomTableScrollbar from '...';
// import { API_BASE_URL, API_TOKEN } from '...';
 const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    width: '6px',
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const BookCategory = () => {
  const theme = useTheme();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const accessToken =
    localStorage.getItem('accessToken') ||
    sessionStorage.getItem('accessToken') ||
    localStorage.getItem('token');
  const API_TOKEN = accessToken ? `Bearer ${accessToken}` : '';

  /* ---------- Organization from session, Branch from navbar ---------- */
  const { selectedBranchId } = useBranch();
  const organizationId = (() => {
    try {
      return JSON.parse(sessionStorage.getItem('user') || '{}').organization_id || '';
    } catch (e) {
      return '';
    }
  })();

  /* ---------- State ---------- */
  const [categories, setCategories] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [serverPaginated, setServerPaginated] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); 
  const [ordering, setOrdering] = useState('category_name');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const rowsPerPageOptions = [5, 10, 25, 50];

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  const [description, setDescription] = useState('');
  const [bulkRows, setBulkRows] = useState([{ category_name: '', description: '' }]);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const requestId = useRef(0); // ignores stale responses

  /* ---------- Helpers ---------- */
  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const resetForm = () => {
    setCategoryName('');
    setDescription('');
    setSelectedCategory(null);
  };

 const bulkFieldSx = {
  '& .MuiInputLabel-root': { color: theme.palette.text.disabled },
  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.text.disabled },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
};

  // Turns any failed response into one readable message
  const getErrorMessage = async (response) => {
    const data = await response.json().catch(() => null);
    if (!data) return `Error ${response.status}: ${response.statusText}`;
    if (data.detail || data.message) return data.detail || data.message;
    const source = data.errors || data;
    const first = Object.entries(source)[0];
    if (first) {
      const [field, msg] = first;
      const text = Array.isArray(msg) ? msg.join(', ') : typeof msg === 'object' ? JSON.stringify(msg) : msg;
      return `${field}: ${text}`;
    }
    return `Error ${response.status}: ${response.statusText}`;
  };

  const jsonHeaders = { Authorization: API_TOKEN, 'Content-Type': 'application/json' };

  /* ---------- Fetch list (server-side search, filter, sort, pagination) ---------- */
  const fetchCategories = useCallback(async () => {
    if (!selectedBranchId || !organizationId || !API_TOKEN) {
      setCategories([]);
      setTotalCount(0);
      return;
    }

    const currentRequest = ++requestId.current;

    try {
      setLoading(true);

      const params = new URLSearchParams();
      params.append('organization', organizationId);
      params.append('branch', selectedBranchId);
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (ordering) params.append('ordering', ordering);
      params.append('page', page);
      params.append('page_size', rowsPerPage);

      const response = await fetch(
        `${API_BASE_URL}/api/library/book-category/list/?${params.toString()}`,
        { method: 'GET', headers: jsonHeaders }
      );

      if (!response.ok) throw new Error(await getErrorMessage(response));

      const data = await response.json();
      if (currentRequest !== requestId.current) return; // a newer request replaced this one

      if (Array.isArray(data)) {
        // API returned a plain array (not paginated) -> paginate on the frontend
        setServerPaginated(false);
        setCategories(data);
        setTotalCount(data.length);
      } else {
        const rows = data.results || data.data || [];
        setServerPaginated(true);
        setCategories(rows);
        setTotalCount(data.count ?? rows.length);
      }
    } catch (error) {
      if (currentRequest !== requestId.current) return;
      console.error('Failed to fetch book categories:', error);
      showSnackbar(error.message || 'Failed to load book categories', 'error');
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchId, organizationId, debouncedSearch, statusFilter, ordering, page, rowsPerPage, API_TOKEN]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Debounce search (waits 400ms after typing stops)
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Go back to page 1 when these change
  useEffect(() => {
    setPage(1);
  }, [selectedBranchId, statusFilter, rowsPerPage, ordering]);

  /* ---------- Pagination ---------- */
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  const displayRows = serverPaginated
    ? categories
    : categories.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handleChangePage = (event, newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
  };

  /* ---------- Sorting ---------- */
  const handleSort = (field) => {
    setOrdering((prev) => (prev === field ? `-${field}` : field));
  };

  /* ---------- Create ---------- */
  const handleAddCategory = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleSave = async () => {
    if (!organizationId) return showSnackbar('Organization not found in session', 'error');
    if (!selectedBranchId) return showSnackbar('Please select a branch from the navbar', 'error');
    if (!categoryName.trim()) return showSnackbar('Category Name is required', 'error');

    try {
      setSaving(true);

      const payload = {
        organization: Number(organizationId),
        branch: Number(selectedBranchId),
        category_name: categoryName.trim(),
        description: description.trim(),
      };

      const response = await fetch(`${API_BASE_URL}/api/library/book-category/create/`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(await getErrorMessage(response));

      showSnackbar('Book category added successfully!');
      setShowAddModal(false);
      resetForm();
      await fetchCategories();
    } catch (error) {
      console.error('Create category error:', error);
      showSnackbar(error.message || 'Failed to add book category', 'error');
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Bulk create ---------- */
  const openBulkModal = () => {
    setBulkRows([{ category_name: '', description: '' }]);
    setShowBulkModal(true);
  };

  const updateBulkRow = (index, field, value) => {
    setBulkRows((rows) => rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const addBulkRow = () => {
    setBulkRows((rows) => [...rows, { category_name: '', description: '' }]);
  };

  const removeBulkRow = (index) => {
    setBulkRows((rows) => (rows.length === 1 ? rows : rows.filter((_, i) => i !== index)));
  };

  const handleBulkSave = async () => {
    if (!selectedBranchId) return showSnackbar('Please select a branch from the navbar', 'error');

    const cleaned = bulkRows
      .map((r) => ({ category_name: r.category_name.trim(), description: r.description.trim() }))
      .filter((r) => r.category_name);

    if (cleaned.length === 0) return showSnackbar('Enter at least one category name', 'error');

    // API rejects duplicate names inside the same request
    const lower = cleaned.map((r) => r.category_name.toLowerCase());
    if (new Set(lower).size !== lower.length) {
      return showSnackbar('Duplicate category names found in the list', 'error');
    }

    try {
      setSaving(true);

      const payload = {
        organization: Number(organizationId),
        branch: Number(selectedBranchId),
        categories: cleaned,
      };

      const response = await fetch(`${API_BASE_URL}/api/library/book-category/bulk-create/`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(await getErrorMessage(response));

      const result = await response.json().catch(() => null);
      console.log('Bulk create response:', result);

      showSnackbar(result?.message || 'Bulk create completed. Existing duplicates were skipped.');
      setShowBulkModal(false);
      await fetchCategories();
    } catch (error) {
      console.error('Bulk create error:', error);
      showSnackbar(error.message || 'Failed to bulk create categories', 'error');
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Edit ---------- */
  const handleEdit = (category) => {
    setSelectedCategory(category);
    setCategoryName(category.category_name || category.name || '');
    setDescription(category.description || '');
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!selectedCategory?.id) return showSnackbar('Category ID is missing', 'error');
    if (!categoryName.trim()) return showSnackbar('Category Name is required', 'error');

    try {
      setSaving(true);

      const payload = {
        organization: Number(organizationId),
        branch: Number(selectedBranchId),
        category_name: categoryName.trim(),
        description: description.trim(),
      };

      const response = await fetch(
        `${API_BASE_URL}/api/library/book-category/update/${selectedCategory.id}/`,
        { method: 'PUT', headers: jsonHeaders, body: JSON.stringify(payload) }
      );

      if (!response.ok) throw new Error(await getErrorMessage(response));

      showSnackbar('Book category updated successfully!');
      setShowEditModal(false);
      resetForm();
      await fetchCategories();
    } catch (error) {
      console.error('Update category error:', error);
      showSnackbar(error.message || 'Failed to update book category', 'error');
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Activate / Deactivate ---------- */
  const handleToggleStatus = async (category) => {
    const newStatus = !(category.is_active !== false);

    try {
      setTogglingId(category.id);

      const response = await fetch(
        `${API_BASE_URL}/api/library/book-category/status/${category.id}/`,
        { method: 'PATCH', headers: jsonHeaders, body: JSON.stringify({ is_active: newStatus }) }
      );

      if (!response.ok) throw new Error(await getErrorMessage(response));

      setCategories((prev) =>
        prev.map((c) => (c.id === category.id ? { ...c, is_active: newStatus } : c))
      );
      showSnackbar(`Category ${newStatus ? 'activated' : 'deactivated'} successfully!`);

      // If a status filter is on, the row may no longer belong in the list
      if (statusFilter !== 'all') await fetchCategories();
    } catch (error) {
      console.error('Status update error:', error);
      showSnackbar(error.message || 'Failed to update status', 'error');
    } finally {
      setTogglingId(null);
    }
  };

  /* ---------- Delete ---------- */
  const handleShowDeleteModal = (id, name) => {
    setSelectedCategory({ id, category_name: name });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    if (!deleting) {
      setShowDeleteModal(false);
      setSelectedCategory(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedCategory?.id) return;

    try {
      setDeleting(true);

      const response = await fetch(
        `${API_BASE_URL}/api/library/book-category/delete/${selectedCategory.id}/`,
        { method: 'DELETE', headers: jsonHeaders }
      );

      if (!response.ok) throw new Error(await getErrorMessage(response));

      showSnackbar('Book category deleted successfully!');
      setShowDeleteModal(false);
      setSelectedCategory(null);

      // If we deleted the last row on this page, step back one page
      if (displayRows.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        await fetchCategories();
      }
    } catch (error) {
      console.error('Delete category error:', error);
      showSnackbar(error.message || 'Failed to delete book category', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    if (!saving) {
      setShowEditModal(false);
      resetForm();
    }
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        py: 2,
      }}
    >
      {/* =====================================================
          SEARCH + ADD BUTTON
      ===================================================== */}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          p: 2,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {/* Search */}

          <TextField
            variant="outlined"
            placeholder="Search by Category Name"
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(e.target.value)
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color:
                        theme.palette.primary.main,
                      marginLeft: 1.5,
                      fontSize: 24,
                    }}
                  />
                </InputAdornment>
              ),

              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setSearchTerm('')
                    }
                    sx={{
                      color:
                        theme.palette.text.disabled,
                      marginRight: 1,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),

              sx: {
                borderRadius: '30px',

                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.08)'
                    : '#fff',

                boxShadow:
                  theme.palette.mode === 'dark'
                    ? '0 2px 8px rgba(0,0,0,0.3)'
                    : '0 2px 8px rgba(0,0,0,0.1)',

                '& .MuiOutlinedInput-notchedOutline':
                  {
                    border:
                      theme.palette.mode ===
                      'dark'
                        ? '1px solid rgba(255,255,255,0.18)'
                        : '1px solid rgba(0,0,0,0.1)',
                  },

                '&:hover': {
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.12)'
                      : '#f8f8f8',
                },

                '&.Mui-focused': {
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.1)'
                      : '#fff',

                  boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,

                  '& .MuiOutlinedInput-notchedOutline':
                    {
                      border: `2px solid ${theme.palette.primary.main}`,
                    },
                },

                paddingLeft: 1.5,
                height: '50px',
                transition:
                  'all 0.3s ease',
              },
            }}
            sx={{
              width: 350,
              maxWidth: '100%',

              '& .MuiInputBase-input': {
                padding: '10px 14px',
                color:
                  theme.palette.text.primary,
                fontWeight: 500,
              },
            }}
          />

          {/* Add button */}

          <Button
            variant="contained"
            onClick={handleAddCategory}
            sx={{
              ml: 2,
              width: '200px',
              borderRadius: '10px',
              height: '50px',
              padding: '10px 0',
              fontWeight: 'bold',
              backgroundColor:
                theme.palette.primary.main,

              '&:hover': {
                backgroundColor:
                  theme.palette.primary.dark,
              },
            }}
            startIcon={<AddIcon />}
          >
            ADD BOOK CATEGORY
          </Button>

                    <Button
            variant="contained"
            onClick={openBulkModal}
            startIcon={<BulkAddIcon />}
            sx={{ borderRadius: '10px', height: '50px', fontWeight: 'bold', px: 3 }}
          >
            BULK ADD
          </Button>

        <FormControl size="small" sx={{ minWidth: 170 }}>
  <Select
    value={statusFilter}
    onChange={(e) => setStatusFilter(e.target.value)}
    renderValue={(v) => `Status: ${v === 'all' ? 'All' : v === 'true' ? 'Active' : 'Inactive'}`}
    sx={{ borderRadius: '30px', height: '50px' }}
  >
    <MenuItem value="all">All</MenuItem>
    <MenuItem value="true">Active</MenuItem>
    <MenuItem value="false">Inactive</MenuItem>
  </Select>
</FormControl>
        </Box>

        {/* User guide */}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={() =>
              setShowUserGuideModal(true)
            }
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              minWidth: 0,
              color:
                theme.palette.primary.main,
            }}
          >
            <InfoOutlineIcon
              sx={{ fontSize: 30 }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* =====================================================
          TABLE
      ===================================================== */}

      <Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor:
            theme.palette.background.paper,
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
         <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
         <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
                      <Table
                        sx={{
                          minWidth: 1200,
                          '& .MuiTableHead-root .MuiTableCell-root': {
                            fontWeight: 'bold',
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                          },
                        }}
                      >
                        <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>

                <TableCell sortDirection={ordering.replace('-', '') === 'category_name' ? (ordering.startsWith('-') ? 'desc' : 'asc') : false}>
                  <TableSortLabel
                    active={ordering.replace('-', '') === 'category_name'}
                    direction={ordering === '-category_name' ? 'desc' : 'asc'}
                    onClick={() => handleSort('category_name')}
                    sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                  >
                    Category Name
                  </TableSortLabel>
                </TableCell>

                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>

                <TableCell sortDirection={ordering.replace('-', '') === 'created_at' ? (ordering.startsWith('-') ? 'desc' : 'asc') : false}>
                  <TableSortLabel
                    active={ordering.replace('-', '') === 'created_at'}
                    direction={ordering === '-created_at' ? 'desc' : 'asc'}
                    onClick={() => handleSort('created_at')}
                    sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                  >
                    Created On
                  </TableSortLabel>
                </TableCell>

                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{ py: 5 }}
                  >
                    <CircularProgress
                      size={30}
                    />
                  </TableCell>
                </TableRow>
              ) : displayRows.length >
                0 ? (
                displayRows.map(
                  (category, index) => {
                    const categoryName =
                      category.category_name ||
                      category.name ||
                      '—';

                    const isActive =
                      category.is_active !==
                      false;

                    return (
                      <TableRow
                        key={category.id}
                        sx={{
                          '&:hover': {
                            backgroundColor:
                              theme.palette
                                .action.hover,
                          },
                        }}
                      >
                        <TableCell>
                          {(page - 1) *
                            rowsPerPage +
                            index +
                            1}
                        </TableCell>

                        <TableCell
                          sx={{
                            fontWeight: 600,
                          }}
                        >
                          {categoryName}
                        </TableCell>

                        <TableCell
                          sx={{
                            maxWidth: 400,
                            wordBreak:
                              'break-word',
                          }}
                        >
                          {category.description ||
                            '—'}
                        </TableCell>

                                               <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Tooltip title={isActive ? 'Click to deactivate' : 'Click to activate'}>
                              <span>
                                <Switch
                                  size="small"
                                  checked={isActive}
                                  disabled={togglingId === category.id}
                                  onChange={() => handleToggleStatus(category)}
                                />
                              </span>
                            </Tooltip>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: isActive ? '#2e7d32' : '#d32f2f' }}
                            >
                              {isActive ? 'Active' : 'Inactive'}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          {category.created_at
                            ? new Date(category.created_at).toLocaleDateString()
                            : '—'}
                        </TableCell>

                        <TableCell align="center">
                          {/* Edit */}

                          <IconButton
                            onClick={() =>
                              handleEdit(
                                category
                              )
                            }
                            sx={{
                              color:
                                theme.palette
                                  .primary
                                  .light,

                              '&:hover': {
                                backgroundColor:
                                  theme.palette
                                    .action
                                    .hover,
                              },
                            }}
                          >
                            <EditIcon />
                          </IconButton>

                          {/* Delete */}

                          <IconButton
                            color="error"
                            onClick={() =>
                              handleShowDeleteModal(
                                category.id,
                                categoryName
                              )
                            }
                            sx={{
                              '&:hover': {
                                backgroundColor:
                                  theme.palette
                                    .action
                                    .hover,
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  }
                )
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{
                      fontSize: '1rem',
                      padding: '30px',
                    }}
                  >
                    No Data Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </CustomTableScrollbar>
      </Paper>

      {/* =====================================================
          PAGINATION
      ===================================================== */}

            {totalCount > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent:
              'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color:
                theme.palette.primary.main,
            }}
          >
            Page <strong>{page}</strong> of{' '}
            <strong>{totalPages}</strong>
          </Typography>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
          >
            {/* Previous */}

            <IconButton
              onClick={() =>
                handleChangePage(
                  null,
                  page - 1
                )
              }
              disabled={page === 1}
              sx={{
                color:
                  theme.palette.primary.main,

                '&:hover': {
                  backgroundColor:
                    theme.palette.action
                      .hover,
                },

                '&.Mui-disabled': {
                  opacity: 0.3,
                },

                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronLeft />
            </IconButton>

            {/* Page numbers */}

            {[
              ...Array(totalPages),
            ].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() =>
                  handleChangePage(
                    null,
                    index + 1
                  )
                }
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',

                  backgroundColor:
                    page === index + 1
                      ? theme.palette
                          .primary.main
                      : 'transparent',

                  color:
                    page === index + 1
                      ? theme.palette
                          .primary
                          .contrastText
                      : theme.palette
                          .text.primary,

                  '&:hover': {
                    backgroundColor:
                      page === index + 1
                        ? theme.palette
                            .primary.dark
                        : theme.palette
                            .action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}

            {/* Next */}

            <IconButton
              onClick={() =>
                handleChangePage(
                  null,
                  page + 1
                )
              }
              disabled={page >= totalPages}
              sx={{
                color:
                  theme.palette.primary.main,

                '&:hover': {
                  backgroundColor:
                    theme.palette.action
                      .hover,
                },

                '&.Mui-disabled': {
                  opacity: 0.3,
                },

                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronRight />
            </IconButton>

            {/* Rows */}

            <FormControl
              size="small"
              sx={{
                minWidth: 100,
                ml: 1,
              }}
            >
              <InputLabel
                id="rows-per-page-label"
                sx={{
                  color:
                    theme.palette.text
                      .primary,
                }}
              >
                Rows
              </InputLabel>

              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={
                  handleChangeRowsPerPage
                }
                label="Rows"
                sx={{
                  height: '36px',
                  color:
                    theme.palette.text
                      .primary,

                  '& .MuiOutlinedInput-notchedOutline':
                    {
                      borderColor:
                        theme.palette
                          .divider,
                    },

                  '&:hover .MuiOutlinedInput-notchedOutline':
                    {
                      borderColor:
                        theme.palette
                          .primary.main,
                    },
                }}
              >
                {rowsPerPageOptions.map(
                  (option) => (
                    <MenuItem
                      key={option}
                      value={option}
                    >
                      {option}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      {/* =====================================================
          ADD CATEGORY MODAL
      ===================================================== */}

      <Modal
        open={showAddModal}
        onClose={() =>
          !saving &&
          setShowAddModal(false)
        }
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform:
              'translate(-50%, -50%)',
            width: 500,
            maxWidth: '90%',
            bgcolor:
              theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}

          <Box
            sx={{
              backgroundColor:
                theme.palette.primary.main,
              padding: '14px 18px',
              display: 'flex',
              justifyContent:
                'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight: 'bold',
              }}
            >
              Add Book Category
            </Typography>

            <IconButton
              onClick={() =>
                !saving &&
                setShowAddModal(false)
              }
              sx={{
                color: '#fff',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Form */}

          <Box sx={{ p: 3 }}>
            <TextField
              label="Category Name"
              variant="outlined"
              fullWidth
              value={categoryName}
              onChange={(e) =>
                setCategoryName(
                  e.target.value
                )
              }
              sx={{
                mb: 2,

                '& .MuiInputLabel-root':
                  {
                    color:
                      theme.palette.text
                        .primary,
                  },

                '& .MuiInputLabel-root.Mui-focused':
                  {
                    color:
                      theme.palette.primary
                        .main,
                  },
              }}
            />

            <TextField
              label="Description"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={description}
              onChange={(e) =>
                setDescription(
                  e.target.value
                )
              }
              sx={{
                mb: 3,

                '& .MuiInputLabel-root':
                  {
                    color:
                      theme.palette.text
                        .primary,
                  },

                '& .MuiInputLabel-root.Mui-focused':
                  {
                    color:
                      theme.palette.primary
                        .main,
                  },
              }}
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent:
                  'flex-end',
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={
                  !categoryName.trim() ||
                  saving
                }
                sx={{
                  backgroundColor:
                    theme.palette.primary
                      .main,

                  '&:hover': {
                    backgroundColor:
                      theme.palette.primary
                        .dark,
                  },
                }}
              >
                {saving ? (
                  <CircularProgress
                    size={22}
                    color="inherit"
                  />
                ) : (
                  'SAVE'
                )}
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={resetForm}
                disabled={saving}
              >
                RESET
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* =====================================================
          EDIT CATEGORY MODAL
      ===================================================== */}

      <Modal
        open={showEditModal}
        onClose={handleCancel}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform:
              'translate(-50%, -50%)',
            width: 500,
            maxWidth: '90%',
            bgcolor:
              theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}

          <Box
            sx={{
              backgroundColor:
                theme.palette.primary.main,
              padding: '14px 18px',
              display: 'flex',
              justifyContent:
                'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight: 'bold',
              }}
            >
              Edit Book Category
            </Typography>

            <IconButton
              onClick={handleCancel}
              sx={{
                color: '#fff',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Form */}

          <Box sx={{ p: 3 }}>
            <TextField
              label="Category Name"
              variant="outlined"
              fullWidth
              value={categoryName}
              onChange={(e) =>
                setCategoryName(
                  e.target.value
                )
              }
              sx={{
                mb: 2,

                '& .MuiInputLabel-root':
                  {
                    color:
                      theme.palette.text
                        .primary,
                  },

                '& .MuiInputLabel-root.Mui-focused':
                  {
                    color:
                      theme.palette.primary
                        .main,
                  },
              }}
            />

            <TextField
              label="Description"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={description}
              onChange={(e) =>
                setDescription(
                  e.target.value
                )
              }
              sx={{
                mb: 3,

                '& .MuiInputLabel-root':
                  {
                    color:
                      theme.palette.text
                        .primary,
                  },

                '& .MuiInputLabel-root.Mui-focused':
                  {
                    color:
                      theme.palette.primary
                        .main,
                  },
              }}
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent:
                  'flex-end',
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                onClick={handleUpdate}
                disabled={
                  !categoryName.trim() ||
                  saving
                }
                sx={{
                  backgroundColor:
                    theme.palette.primary
                      .main,

                  '&:hover': {
                    backgroundColor:
                      theme.palette.primary
                        .dark,
                  },
                }}
              >
                {saving ? (
                  <CircularProgress
                    size={22}
                    color="inherit"
                  />
                ) : (
                  'UPDATE'
                )}
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={handleCancel}
                disabled={saving}
              >
                CANCEL
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* =====================================================
          DELETE MODAL
      ===================================================== */}

      <Modal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform:
              'translate(-50%, -50%)',
            width: 400,
            maxWidth: '90%',
            bgcolor:
              theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette.primary.main,
              padding: '12px 16px',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
              }}
            >
              Delete Confirmation ⚠️
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            <Typography sx={{ mb: 2 }}>
              Are you sure you want to delete{' '}
              <strong>
                {
                  selectedCategory?.category_name
                }
              </strong>
              ?
            </Typography>

            <Box
              sx={{
                display: 'flex',
                justifyContent:
                  'flex-end',
                gap: 1,
                mt: 3,
              }}
            >
              <Button
                onClick={
                  handleCloseDeleteModal
                }
                variant="outlined"
                disabled={deleting}
               data-app-cancel="true">
                Cancel
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <CircularProgress
                    size={22}
                    color="inherit"
                  />
                ) : (
                  'Yes, Delete'
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

           {/* =====================================================
    BULK ADD MODAL
===================================================== */}

<Modal open={showBulkModal} onClose={() => !saving && setShowBulkModal(false)}>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 700,
      maxWidth: '92%',
      maxHeight: '90vh',
      bgcolor: theme.palette.background.paper,
      borderRadius: 2,
      boxShadow: 24,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    {/* Header (fixed) */}
    <Box
      sx={{
        backgroundColor: theme.palette.primary.main,
        p: '14px 18px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
        Bulk Add Book Categories
      </Typography>
      <IconButton onClick={() => !saving && setShowBulkModal(false)} sx={{ color: '#fff' }}>
        <CloseIcon />
      </IconButton>
    </Box>

    {/* Body (scrollable) */}
    <CustomTableScrollbar
      style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 24 }}
    >
      <Typography variant="body2" sx={{ mb: 2 }}>
        Names must be unique in this list. Categories that already exist in this
        branch are skipped automatically.
      </Typography>

      {bulkRows.map((row, index) => (
  <Box key={index} sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'flex-start' }}>
    <TextField
      label={`Category Name ${index + 1}`}
      size="small"
      value={row.category_name}
      onChange={(e) => updateBulkRow(index, 'category_name', e.target.value)}
      inputProps={{ maxLength: 150 }}
      sx={{ flex: 1, ...bulkFieldSx }}
    />
    <TextField
      label="Description"
      size="small"
      value={row.description}
      onChange={(e) => updateBulkRow(index, 'description', e.target.value)}
      sx={{ flex: 1.4, ...bulkFieldSx }}
    />
    <IconButton
      color="error"
      onClick={() => removeBulkRow(index)}
      disabled={bulkRows.length === 1}
    >
      <DeleteIcon />
    </IconButton>
  </Box>
))}

      <Button startIcon={<AddIcon />} onClick={addBulkRow}>
        Add another row
      </Button>
    </CustomTableScrollbar>

    {/* Footer (fixed) */}
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1,
        p: 2,
        flexShrink: 0,
        borderTop: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Button
        variant="contained"
        onClick={handleBulkSave}
        disabled={saving || !selectedBranchId}
      >
        {saving ? <CircularProgress size={22} color="inherit" /> : 'SAVE ALL'}
      </Button>
      <Button
        variant="contained"
        color="error"
        onClick={() => setShowBulkModal(false)}
        disabled={saving}
      >
        CANCEL
      </Button>
    </Box>
  </Box>
</Modal>

      {/* =====================================================
          USER GUIDE
      ===================================================== */}

      <Modal
        open={showUserGuideModal}
        onClose={() =>
          setShowUserGuideModal(false)
        }
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform:
              'translate(-50%, -50%)',
            width: 900,
            maxWidth: '90%',
            bgcolor:
              theme.palette.background.paper,
            borderRadius: '16px',
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}

          <Box
            sx={{
              backgroundColor:
                theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent:
                'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: '#fff',
                fontWeight: 'bold',
              }}
            >
              Book Category Management -
              User Guide
            </Typography>

            <IconButton
              onClick={() =>
                setShowUserGuideModal(false)
              }
              sx={{
                color: '#fff',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}

          <CustomTableScrollbar style={{ maxHeight: '70vh', overflowY: 'auto', padding: 24 }}>
            <Typography
              sx={{
                fontWeight: 'bold',
                color:
                  theme.palette.primary.main,
                mb: 1,
              }}
            >
              ➤ Add a new Book Category
            </Typography>

            <ul
              style={{
                lineHeight: '1.8',
              }}
            >
              <li>
                Click the{' '}
                <strong>
                  "ADD BOOK CATEGORY"
                </strong>{' '}
                button.
              </li>

              <li>
                Enter the{' '}
                <strong>
                  Category Name
                </strong>
                .
              </li>

              <li>
                Enter an optional{' '}
                <strong>
                  Description
                </strong>
                .
              </li>

              <li>
                Click{' '}
                <strong>SAVE</strong> to
                create the category.
              </li>
            </ul>

            <Typography
              sx={{
                fontWeight: 'bold',
                color:
                  theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Update a Book Category
            </Typography>

            <ul
              style={{
                lineHeight: '1.8',
              }}
            >
              <li>
                Find the category in the
                table.
              </li>

              <li>
                Click the{' '}
                <strong>Edit</strong> icon.
              </li>

              <li>
                Update the Category Name
                or Description.
              </li>

              <li>
                Click{' '}
                <strong>UPDATE</strong>.
              </li>
            </ul>

            <Typography
              sx={{
                fontWeight: 'bold',
                color:
                  theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Delete a Book Category
            </Typography>

            <ul
              style={{
                lineHeight: '1.8',
              }}
            >
              <li>
                Click the{' '}
                <strong>Delete</strong> icon.
              </li>

              <li>
                Verify the category name.
              </li>

              <li>
                Click{' '}
                <strong>
                  Yes, Delete
                </strong>
                .
              </li>
            </ul>

            <Typography
              sx={{
                fontWeight: 'bold',
                color:
                  theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Search Categories
            </Typography>

            <ul
              style={{
                lineHeight: '1.8',
              }}
            >
              <li>
                Use the search bar.
              </li>

              <li>
                Search by Category Name
                or Description.
              </li>

              <li>
                Results update automatically.
              </li>
            </ul>

            <Typography
              sx={{
                fontWeight: 'bold',
                color:
                  theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Pagination
            </Typography>

            <ul
              style={{
                lineHeight: '1.8',
              }}
            >
              <li>
                Use previous/next buttons.
              </li>

              <li>
                Select a page number.
              </li>

              <li>
                Change rows per page from
                the dropdown.
              </li>
            </ul>
          </CustomTableScrollbar>
        </Box>
      </Modal>

      {/* =====================================================
          SNACKBAR
      ===================================================== */}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() =>
          setOpenSnackbar(false)
        }
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert
          onClose={() =>
            setOpenSnackbar(false)
          }
          severity={snackbarSeverity}
          sx={{
            width: '100%',
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BookCategory;